// src/utils/api/errors.ts
// Locked

// Error codes for different scenarios
export const ERROR_CODES = {
    // Network related
    NETWORK_OFFLINE: 'ERR_NETWORK_OFFLINE',
    TIMEOUT: 'ERR_TIMEOUT',
    CORS: 'ERR_CORS',

    // HTTP status related
    BAD_REQUEST: 'ERR_BAD_REQUEST',
    UNAUTHORIZED: 'ERR_UNAUTHORIZED',
    FORBIDDEN: 'ERR_FORBIDDEN',
    NOT_FOUND: 'ERR_NOT_FOUND',
    SERVER_ERROR: 'ERR_SERVER',

    // Validation/Parsing related
    VALIDATION: 'ERR_VALIDATION',
    PARSE_ERROR: 'ERR_PARSE',

    // Other
    UNKNOWN: 'ERR_UNKNOWN',
    ABORTED: 'ERR_ABORTED'
} as const;

export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];

// Categories of errors
export type ErrorCategory =
    | 'network'     // Connection, timeout, CORS
    | 'http'        // HTTP status errors
    | 'validation'  // Data validation errors
    | 'parse'       // Response parsing errors
    | 'user'        // User-related errors
    | 'system'      // System/internal errors
    | 'unknown';    // Unclassified errors

// Request context for error tracking
export interface RequestContext {
    url: string;
    method: string;
    timestamp: number;
    retryAttempt: number;
    requestId: string;
    duration?: number;
}

// Validation error structure
export interface ValidationError {
    field: string;
    message: string;
    code: string;
    value?: any;
}

// Enhanced base error structure
export interface APIErrorDetails {
    code: ErrorCode;
    category: ErrorCategory;
    message: string;
    status?: number;
    timestamp: number;
    requestContext?: RequestContext;
    validationErrors?: ValidationError[];
    originalError?: Error;
    metadata?: Record<string, any>;
}

// Main error class
export class APIError extends Error {
    readonly code: ErrorCode;
    readonly category: ErrorCategory;
    readonly status?: number;
    readonly timestamp: number;
    readonly requestContext?: RequestContext;
    readonly validationErrors?: ValidationError[];
    readonly originalError?: Error;
    readonly metadata?: Record<string, any>;

    constructor(details: APIErrorDetails) {
        super(details.message);
        this.name = 'APIError';
        this.code = details.code;
        this.category = details.category;
        this.status = details.status;
        this.timestamp = details.timestamp;
        this.requestContext = details.requestContext;
        this.validationErrors = details.validationErrors;
        this.originalError = details.originalError;
        this.metadata = details.metadata;

        // Ensure proper prototype chain
        Object.setPrototypeOf(this, APIError.prototype);
    }

    // Helper to check if error is validation error
    isValidationError(): boolean {
        return this.category === 'validation' && Boolean(this.validationErrors?.length);
    }

    // Helper to check if error is network related
    isNetworkError(): boolean {
        return this.category === 'network';
    }

    // Convert error to plain object for logging/serialization
    toJSON(): Record<string, any> {
        return {
            name: this.name,
            code: this.code,
            category: this.category,
            message: this.message,
            status: this.status,
            timestamp: this.timestamp,
            requestContext: this.requestContext,
            validationErrors: this.validationErrors,
            metadata: this.metadata,
            stack: this.stack
        };
    }
}

// Error factory functions for common scenarios
export const createAPIError = {
    network: (message: string, requestContext?: RequestContext, originalError?: Error): APIError => {
        return new APIError({
            code: ERROR_CODES.NETWORK_OFFLINE,
            category: 'network',
            message,
            timestamp: Date.now(),
            requestContext,
            originalError
        });
    },

    timeout: (requestContext?: RequestContext): APIError => {
        return new APIError({
            code: ERROR_CODES.TIMEOUT,
            category: 'network',
            message: 'Request timed out',
            timestamp: Date.now(),
            requestContext
        });
    },

    validation: (errors: ValidationError[], requestContext?: RequestContext): APIError => {
        const message = errors.length === 1
            ? errors[0].message
            : `${errors.length} validation errors occurred`;

        return new APIError({
            code: ERROR_CODES.VALIDATION,
            category: 'validation',
            message,
            timestamp: Date.now(),
            requestContext,
            validationErrors: errors
        });
    },

    http: (status: number, message: string, requestContext?: RequestContext): APIError => {
        const code = status >= 500 ? ERROR_CODES.SERVER_ERROR :
            status === 404 ? ERROR_CODES.NOT_FOUND :
                status === 403 ? ERROR_CODES.FORBIDDEN :
                    status === 401 ? ERROR_CODES.UNAUTHORIZED :
                        ERROR_CODES.BAD_REQUEST;

        return new APIError({
            code,
            category: 'http',
            message,
            status,
            timestamp: Date.now(),
            requestContext
        });
    },

    parse: (message: string, requestContext?: RequestContext, originalError?: Error): APIError => {
        return new APIError({
            code: ERROR_CODES.PARSE_ERROR,
            category: 'parse',
            message,
            timestamp: Date.now(),
            requestContext,
            originalError
        });
    }
};

// Helper functions for error detection
export const errorHelpers = {
    isCORSError: (error: Error): boolean => {
        return error instanceof TypeError && (
            error.message.includes('CORS') ||
            error.message.includes('cross-origin')
        );
    },

    isNetworkError: (error: Error): boolean => {
        return error instanceof TypeError && (
            error.message.includes('network') ||
            error.message.includes('Network') ||
            error.message.includes('Failed to fetch')
        );
    },

    isTimeoutError: (error: Error): boolean => {
        return error.name === 'AbortError' ||
            error.message.includes('timeout') ||
            error.message.includes('Timeout');
    },

    // Helper to extract validation errors from various API response formats
    extractValidationErrors: (data: any): ValidationError[] => {
        if (!data) return [];

        // Handle array of validation errors
        if (Array.isArray(data.errors)) {
            return data.errors.map((err: any) => ({
                field: err.field || 'unknown',
                message: err.message || 'Invalid value',
                code: err.code || 'INVALID_VALUE',
                value: err.value
            }));
        }

        // Handle object with field keys
        if (typeof data === 'object' && data.errors) {
            return Object.entries(data.errors).map(([field, message]) => ({
                field,
                message: String(message),
                code: 'INVALID_VALUE',
                value: data[field]
            }));
        }

        return [];
    }
};